import type { UserNode } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import { buildTreeFilterNodes, wrapExprNode } from './node-builders';
import { TalentDiscoveryV3Tags } from './types';

const getLevelIdToTagMap = (
  isCurrent: boolean
): Record<string, TalentDiscoveryV3Tags> => {
  return isCurrent
    ? {
        region: TalentDiscoveryV3Tags.UserRegion,
        country: TalentDiscoveryV3Tags.UserCountry,
        state: TalentDiscoveryV3Tags.UserState,
        msa: TalentDiscoveryV3Tags.UserMsa,
        city: TalentDiscoveryV3Tags.UserCity,
      }
    : {
        region: TalentDiscoveryV3Tags.Region,
        country: TalentDiscoveryV3Tags.Country,
        msa: TalentDiscoveryV3Tags.Msa,
        city: TalentDiscoveryV3Tags.City,
      };
};

/**
 * Build geography filter node from filter state.
 * Uses the generic buildTreeFilterNodes function to handle tree filter logic.
 * When isCurrent is false, uses non-User tags (Region/Country/Msa/City).
 * When isCurrent is true (default), uses User tags (UserRegion/UserCountry/UserMsa/UserCity).
 */
export const buildGeographyFilter = (filters: Filter[]): UserNode | null => {
  const geographyFilter = findFilterById(filters, FilterId.GEOGRAPHY);
  if (!geographyFilter || geographyFilter.type !== FilterType.TREE) {
    return null;
  }

  const isCurrent = geographyFilter.isCurrent ?? true;

  const levelIdToTagMap = getLevelIdToTagMap(isCurrent);

  const exprNode = buildTreeFilterNodes(geographyFilter, levelIdToTagMap);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, geographyFilter.isNot);
};

/**
 * Build multi geography filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 * When isCurrent is false for an instance, uses non-User tags (Region/Country/Msa/City).
 * When isCurrent is true (default), uses User tags (UserRegion/UserCountry/UserMsa/UserCity).
 */
export const buildMultiGeographyFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiGeography = findFilterById(filters, FilterId.MULTI_GEOGRAPHY);

  if (!multiGeography || !isMultiFilter(multiGeography)) {
    return null;
  }

  if (multiGeography.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiGeography.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const isCurrent = instance.isCurrent ?? true;

      const levelIdToTagMap = getLevelIdToTagMap(isCurrent);

      const exprNode = buildTreeFilterNodes(instance, levelIdToTagMap);
      if (!exprNode) return null;

      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
